import React from 'react';
import { Link } from 'react-router-dom';

import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';

type AdminArea = {
  id: string;
  title: string;
  description: string;
  path: string;
  cta: string;
};

const ADMIN_AREAS: AdminArea[] = [
  {
    id: 'analytics',
    title: 'Analytics',
    description: 'Review app load times, device usage, engagement, module performance, and user-level analytics.',
    path: '/admin/analytics',
    cta: 'Open Analytics',
  },
  {
    id: 'content',
    title: 'Content',
    description: 'Build media, questions, modules, courses, imports, exports, and quiz QA from one workspace.',
    path: '/admin/content',
    cta: 'Open Content',
  },
  {
    id: 'organizations',
    title: 'Organizations',
    description: 'Manage organization details, signup codes, default content, and links into cohorts.',
    path: '/admin/organizations',
    cta: 'Open Organizations',
  },
  {
    id: 'cohorts',
    title: 'Cohorts',
    description: 'Manage cohorts, member roles, checklist templates, assignments, and cohort-specific overrides.',
    path: '/admin/cohorts',
    cta: 'Open Cohorts',
  },
  {
    id: 'users',
    title: 'Users',
    description: 'Manage user records, manual content overrides, and Module Companion resets.',
    path: '/admin/users',
    cta: 'Open Users',
  },
  {
    id: 'feedback',
    title: 'Feedback',
    description: 'Review internal feedback and issue reports from one workspace.',
    path: '/admin/feedback',
    cta: 'Open Feedback',
  },
  {
    id: 'operations',
    title: 'Operations',
    description: 'Run internal admin actions and future maintenance workflows from one place.',
    path: '/admin/operations',
    cta: 'Open Operations',
  },
  {
    id: 'ai-settings',
    title: 'AI Settings',
    description: 'Choose models, prompts, and voices for every AI feature and review AI usage.',
    path: '/admin/ai-settings',
    cta: 'Open AI Settings',
  },
];

const AdminHome: React.FC = () => (
  <section className="space-y-8 px-6 py-8">
    <div className="max-w-3xl space-y-2">
      <h1 className="text-3xl font-semibold text-foreground">Admin Home</h1>
      <p className="text-sm text-muted-foreground">
        Internal admin workspaces are organized by what you are managing, not by one-off tools.
      </p>
    </div>

    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {ADMIN_AREAS.map((area) => (
        <Card
          key={area.title}
          className="flex h-full w-full max-w-none flex-col"
          maxWidth="none"
          data-testid={`admin-area-${area.id}`}
        >
          <CardHeader>
            <CardTitle>{area.title}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col items-stretch gap-4">
            <p
              className="flex-1 text-sm text-muted-foreground"
              data-testid={`admin-area-description-${area.id}`}
            >
              {area.description}
            </p>
            <Button asChild size="auto" className="self-start">
              <Link to={area.path}>{area.cta}</Link>
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  </section>
);

export default AdminHome;
